import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, 
  Download, 
  Calendar,
  ScrollText,
  BookOpen,
  ExternalLink,
  Loader2,
  BarChart3,
  PieChart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statuten, setStatuten] = useState(null);
  const [protokolle, setProtokolle] = useState([]);
  const [jahresberichte, setJahresberichte] = useState([]);
  const [finanzberichte, setFinanzberichte] = useState([]);
  const [allgemein, setAllgemein] = useState([]);

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API_URL}/api/documents/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatuten(response.data.statuten);
      setProtokolle(response.data.protokolle || []);
      setJahresberichte(response.data.jahresberichte || []);
      setFinanzberichte(response.data.finanzberichte || []);
      setAllgemein(response.data.allgemein || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wider mb-2">
            Dokumente
          </h1>
          <p className="text-muted-foreground">
            Vereinsstatuten, Protokolle, Jahres- und Finanzberichte
          </p>
        </div>

        <div className="space-y-8">
          {/* Downloads Section - first, most prominent */}
          {allgemein.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Download className="w-6 h-6 text-primary" />
                <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider">
                  Downloads
                </h2>
              </div>
              <div className="space-y-3">
                {allgemein.map(doc => (
                  <div key={doc.id} className="bg-card border border-border p-4 sm:p-6 border-primary/50">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="font-bold">{doc.title}</p>
                          <p className="text-sm text-muted-foreground">{doc.filename}</p>
                        </div>
                      </div>
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 font-medium text-sm bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid={`download-allgemein-${doc.id}-btn`}
                      >
                        <Download className="w-4 h-4" /> Herunterladen
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Statuten Section */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <ScrollText className="w-6 h-6 text-primary" />
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider">
                Vereinsstatuten
              </h2>
            </div>
            
            {statuten ? (
              <div className="bg-card border border-border p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold text-lg">{statuten.title}</p>
                      {statuten.description && (
                        <p className="text-muted-foreground text-sm mt-1">{statuten.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Aktualisiert: {new Date(statuten.uploaded_at).toLocaleDateString('de-CH', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <a 
                    href={statuten.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-sm"
                    data-testid="download-statuten-btn"
                  >
                    <Download className="w-5 h-5" />
                    PDF herunterladen
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-card border border-border p-6 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Die Vereinsstatuten werden in Kürze hier verfügbar sein.
                </p>
              </div>
            )}
          </section>

          {/* Protokolle Section */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-6 h-6 text-primary" />
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider">
                GV-Protokolle
              </h2>
            </div>
            
            {protokolle.length > 0 ? (
              <div className="space-y-3">
                {protokolle.map((protokoll, index) => (
                  <div 
                    key={protokoll.id} 
                    className={`bg-card border border-border p-4 sm:p-6 ${index === 0 ? 'border-primary/50' : ''}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 flex items-center justify-center flex-shrink-0 ${
                          index === 0 ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                        }`}>
                          <span className="font-bold text-lg">{protokoll.year}</span>
                        </div>
                        <div>
                          <p className="font-bold">{protokoll.title}</p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(protokoll.date).toLocaleDateString('de-CH', {
                              weekday: 'long',
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                          {protokoll.description && (
                            <p className="text-sm text-muted-foreground mt-2">{protokoll.description}</p>
                          )}
                        </div>
                      </div>
                      <a 
                        href={protokoll.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-4 py-2 font-medium text-sm ${
                          index === 0 
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                            : 'border border-border hover:bg-muted'
                        }`}
                        data-testid={`download-protokoll-${protokoll.year}-btn`}
                      >
                        <Download className="w-4 h-4" />
                        PDF
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-card border border-border p-6 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Die Protokolle der Generalversammlungen werden nach den GVs hier verfügbar sein.
                </p>
              </div>
            )}
          </section>

          {/* Jahresberichte Section */}
          {jahresberichte.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-6 h-6 text-primary" />
                <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider">
                  Jahresberichte
                </h2>
              </div>
              <div className="space-y-3">
                {jahresberichte.map((doc, index) => (
                  <div key={doc.id} className={`bg-card border border-border p-4 sm:p-6 ${index === 0 ? 'border-primary/50' : ''}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 flex items-center justify-center flex-shrink-0 ${index === 0 ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
                          <span className="font-bold text-lg">{doc.year}</span>
                        </div>
                        <div>
                          <p className="font-bold">{doc.title}</p>
                          {doc.description && <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>}
                        </div>
                      </div>
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-4 py-2 font-medium text-sm ${index === 0 ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'border border-border hover:bg-muted'}`}
                        data-testid={`download-jahresbericht-${doc.year}-btn`}
                      >
                        <Download className="w-4 h-4" /> PDF
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Finanzberichte Section */}
          {finanzberichte.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <PieChart className="w-6 h-6 text-primary" />
                <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider">
                  Finanzberichte
                </h2>
              </div>
              <div className="space-y-3">
                {finanzberichte.map((doc, index) => (
                  <div key={doc.id} className={`bg-card border border-border p-4 sm:p-6 ${index === 0 ? 'border-primary/50' : ''}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 flex items-center justify-center flex-shrink-0 ${index === 0 ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
                          <span className="font-bold text-lg">{doc.year}</span>
                        </div>
                        <div>
                          <p className="font-bold">{doc.title}</p>
                          {doc.description && <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>}
                        </div>
                      </div>
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-4 py-2 font-medium text-sm ${index === 0 ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'border border-border hover:bg-muted'}`}
                        data-testid={`download-finanzbericht-${doc.year}-btn`}
                      >
                        <Download className="w-4 h-4" /> PDF
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Info Box */}
          <div className="bg-secondary/30 border border-border p-4 text-sm text-muted-foreground">
            <p>
              <strong>Hinweis:</strong> Alle Dokumente sind vertraulich und nur für Mitglieder des 
              Swiss Historic Racing Team bestimmt. Die Weitergabe an Dritte ist nicht gestattet.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
